import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
// firebase/db not needed in this page
import Header from '../components/Header';
import { api } from '../api';
import {
  Typography,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Toolbar,
} from '@mui/material';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { HomeIcon, SettingsIcon, DeleteIcon, EditIcon, ShareIcon } from '../components/MuiIcons';
import EmojiEventsIcon from '../components/EmojiEventsIcon';
import ListItemButton from '@mui/material/ListItemButton';
 
import Chip from '@mui/material/Chip';

const drawerWidth = 220;

type Competition = {
  id: string;
  name: string;
  description?: string;
  type?: 'Wine' | 'Food';
  maxEntries?: number;
  maxVotesPerPerson?: number;
  role?: 'host' | 'participant';
  isHost?: boolean;
  status?: 'draft' | 'started' | 'stopped' | 'published' | string;
};

const CompetitionsPage: React.FC = () => {
  const history = useHistory();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newType, setNewType] = useState<'Wine' | 'Food'>('Wine');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [maxEntries, setMaxEntries] = useState(10);
  const [maxVotesPerPerson, setMaxVotesPerPerson] = useState(3);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCompetition, setEditingCompetition] = useState<Competition | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareCompetitionId, setShareCompetitionId] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [inviteList, setInviteList] = useState<{ email: string; role: 'host' | 'participant' }[]>([]);
  const [shareError, setShareError] = useState('');
  const [sharing, setSharing] = useState(false);
  const [hostCompetitions, setHostCompetitions] = useState<Competition[]>([]);
  const [guestCompetitions, setGuestCompetitions] = useState<Competition[]>([]);
  const [hostSharedCompetitions, setHostSharedCompetitions] = useState<Competition[]>([]);
  const [allCompetitions, setAllCompetitions] = useState<Competition[]>([]);

  useEffect(() => {
    console.log('[CompetitionsPage] mount: restoring cached competitions and initializing listeners');
    // Restore cached competitions quickly to avoid empty UI during navigation
    try {
      const cached = localStorage.getItem('hostCompetitions');
      if (cached) {
        setHostCompetitions(JSON.parse(cached));
        console.log('[CompetitionsPage] restored hostCompetitions from cache, count=', JSON.parse(cached).length);
      }
    } catch (e) { /* ignore */ }

    const stored = localStorage.getItem('user');
    if (!stored) {
      console.warn('[CompetitionsPage] no stored user found on mount — listeners will not be started');
      // leave cached competitions visible
      return;
    }
    const user = JSON.parse(stored);
    if (!user?.id) {
      console.warn('[CompetitionsPage] stored user missing id — listeners will not be started');
      return;
    }

    setLoading(true);

    const fetchCompetitions = async () => {
      try {
        if (dialogOpen || isSubmitting) return;
        console.log('[CompetitionsPage] fetching competitions from backend for user', user.id);
        const res = await fetch(api(`/api/competitions/${user.id}`));
        if (!res.ok) {
          console.error('[CompetitionsPage] failed to fetch competitions from backend', res.status);
          return;
        }
        const body = await res.json();
        const all: any[] = (body.competitions || []).map((c: any) => ({ ...c }));

        // Split into owner-owned, host-shared, and guest (participant) lists
        // helper to extract milliseconds from Firestore-like timestamps or plain values
        const toMillis = (v: any): number | null => {
          if (!v) return null;
          if (typeof v === 'number') return v;
          if (typeof v === 'string') {
            const parsed = Date.parse(v);
            return Number.isNaN(parsed) ? null : parsed;
          }
          if (typeof v === 'object') {
            // Firestore Timestamp shape
            const seconds = (v.seconds ?? v._seconds ?? null);
            const nanos = (v.nanoseconds ?? v._nanoseconds ?? 0);
            if (seconds != null) return seconds * 1000 + Math.round((nanos || 0) / 1e6);
            // Date-like
            if (typeof (v as any).toMillis === 'function') {
              try { return (v as any).toMillis(); } catch (e) { /* ignore */ }
            }
            if ((v as any).toDate && typeof (v as any).toDate === 'function') {
              try { return (v as any).toDate().getTime(); } catch (e) { /* ignore */ }
            }
          }
          return null;
        };

        const mapWithSort = (c: any, role: 'host' | 'participant', isHost: boolean) => {
          const created = toMillis(c.createdAt) || toMillis(c.created_at) || null;
          const updated = toMillis(c.updatedAt) || toMillis(c.updated_at) || null;
          const shared = toMillis(c.sharedAt) || toMillis(c.shared_at) || null;
          // Host competitions should be ordered by creation time; participant competitions by when they were shared.
          let sortTime = 0;
          if (role === 'host') {
            sortTime = created || updated || shared || 0;
          } else {
            sortTime = shared || updated || created || 0;
          }
          return ({ id: c.id, name: c.name, description: c.description, type: c.type, maxEntries: c.maxEntries, role, isHost, status: c.status || 'draft', sortTime });
        };

        const owned: Competition[] = all.filter(c => c.ownerId === user.id || (c.isHost === true && c.ownerId === user.id)).map((c: any) => mapWithSort(c, 'host', true));
        const hostShared: Competition[] = all.filter(c => (c.isHost === true) && (c.ownerId !== user.id)).map((c: any) => mapWithSort(c, 'host', true));
        const guest: Competition[] = all.filter(c => c.isHost !== true).map((c: any) => mapWithSort(c, 'participant', false));

        // Prefer cached host list when backend returns empty to avoid flashing blank UI
        try {
          if (owned.length === 0) {
            const cached = localStorage.getItem('hostCompetitions');
            if (cached) {
              const parsed: Competition[] = JSON.parse(cached);
              if (parsed && parsed.length > 0) {
                console.log('[CompetitionsPage] backend returned no owned competitions — using cache');
                setHostCompetitions(parsed);
              } else {
                setHostCompetitions(owned);
              }
            } else {
              setHostCompetitions(owned);
            }
          } else {
            setHostCompetitions(owned);
            try { localStorage.setItem('hostCompetitions', JSON.stringify(owned)); } catch (e) { /* ignore */ }
          }
        } catch (e) {
          setHostCompetitions(owned);
        }

        setHostSharedCompetitions(hostShared);
        setGuestCompetitions(guest);

        // Build unified list: owned + hostShared + guest
        // Merge and dedupe by id, prefer the first occurrence
        const combined = Array.from(new Map([...owned, ...hostShared, ...guest].map(c => [c.id, c])).values());
        // Sort by computed sortTime (newest first). Competitions without timestamps sort last.
        combined.sort((a: any, b: any) => (b.sortTime || 0) - (a.sortTime || 0));
        setAllCompetitions(combined);
      } catch (err) {
        console.error('[CompetitionsPage] failed to fetch competitions:', err);
      } finally {
        setLoading(false);
      }
    };

    // Initial load
    fetchCompetitions();

    // Poll for updates so recipients see newly-shared competitions without manual refresh
    const interval = setInterval(fetchCompetitions, 5000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const handleMenuItemClick = (path: string) => {
    history.push(path);
    setDrawerOpen(false);
  };

  const handleOpenDialog = () => {
    setEditingCompetition(null);
    setDialogOpen(true);
  };

  const handleEdit = (competition: Competition) => {
    setEditingCompetition(competition);
    setNewName(competition.name);
    setNewDescription(competition.description || '');
    setNewType(competition.type || 'Wine');
    setMaxEntries(competition.maxEntries || 10);
    setMaxVotesPerPerson(competition.maxVotesPerPerson || 3);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCompetition(null);
    setNewName('');
    setNewDescription('');
    setNewType('Wine');
    setMaxEntries(10);
    setMaxVotesPerPerson(3);
  };

  const handleCreateCompetition = async () => {
    const stored = localStorage.getItem('user');
    if (!stored) {
      setError('Please sign in first.');
      return;
    }
    const user = JSON.parse(stored);
    if (!user?.id) {
      setError('Please sign in again to refresh your session.');
      return;
    }
    if (!newName.trim()) return;
    setError('');
    setSaving(true);
    setIsSubmitting(true);
    try {
      const url = editingCompetition
        ? api(`/api/competitions/${user.id}/${editingCompetition.id}`)
        : api(`/api/competitions/${user.id}`);
      const method = editingCompetition ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newName.trim(),
          description: newDescription.trim(),
          type: newType,
          maxEntries,
          maxVotesPerPerson,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        if (editingCompetition) {
          // Only manually update state for edits
          setHostCompetitions(prev =>
            prev.map(c =>
              c.id === editingCompetition.id
                ? { 
                    ...c, 
                    name: data.name, 
                    description: data.description, 
                    type: data.type, 
                    maxEntries: data.maxEntries,
                    maxVotesPerPerson: data.maxVotesPerPerson 
                  }
                : c
            )
          );
        }
        // For new competitions, the Firestore listener should add it — but also
        // optimistically append the created competition so the UI updates
        // immediately when the backend returns the new competition.
        if (!editingCompetition) {
          try {
            const newComp: Competition = {
              id: data.id,
              name: data.name,
              description: data.description || '',
              type: data.type,
              maxEntries: data.maxEntries,
              isHost: true,
            };
            setHostCompetitions(prev => [newComp, ...prev]);
          } catch (e) {
            // ignore optimistic update failures
          }
        }
        handleCloseDialog();
      }
    } finally {
      setSaving(false);
      setIsSubmitting(false);
    }
  };

  const handleDeleteCompetition = async (competitionId: string) => {
    const stored = localStorage.getItem('user');
    if (!stored) return;
    const user = JSON.parse(stored);
    if (!user?.id) return;

    try {
      const res = await fetch(api(`/api/competitions/${user.id}/${competitionId}`), { method: 'DELETE' });
      if (res.ok) {
        setHostCompetitions(prev => prev.filter(c => c.id !== competitionId));
      }
    } catch {
      // ignore for now
    }
  };

  const handleDeleteSharedCompetition = async (sharedCompetitionId: string) => {
    const stored = localStorage.getItem('user');
    if (!stored) return;
    const user = JSON.parse(stored);
    if (!user?.id) return;

    try {
      const res = await fetch(api(`/api/competitions/${user.id}/shared/${sharedCompetitionId}`), { method: 'DELETE' });
      if (res.ok) {
        setGuestCompetitions(prev => prev.filter(c => c.id !== sharedCompetitionId));
      }
    } catch {
      // ignore for now
    }
  };

  const handleRequestDelete = (competitionId: string, isShared: boolean = false) => {
    setPendingDeleteId(competitionId);
    setConfirmOpen(true);
    (window as any).isDeletingShared = isShared;
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setPendingDeleteId(null);
    (window as any).isDeletingShared = false;
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    
    const isShared = (window as any).isDeletingShared;
    
    if (isShared) {
      await handleDeleteSharedCompetition(pendingDeleteId);
    } else {
      await handleDeleteCompetition(pendingDeleteId);
    }
    
    setConfirmOpen(false);
    setPendingDeleteId(null);
    (window as any).isDeletingShared = false;
  };

  const handleOpenShareDialog = (competitionId: string) => {
    setShareCompetitionId(competitionId);
    setInviteList([]);
    setEmailInput('');
    setShareError('');
    setShareDialogOpen(true);
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setShareCompetitionId(null);
    setInviteList([]);
    setEmailInput('');
    setShareError('');
  };

  const handleAddEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setShareError('Please enter a valid email address');
      return;
    }

    if (inviteList.some(i => i.email === email)) {
      setShareError('Email already added');
      return;
    }

    setInviteList(prev => [...prev, { email, role: 'participant' }]);
    setEmailInput('');
    setShareError('');
  };

  const handleRemoveEmail = (email: string) => {
    setInviteList(prev => prev.filter(e => e.email !== email));
  };

  const handleChangeRole = (email: string, role: 'host' | 'participant') => {
    setInviteList(prev => prev.map(i => i.email === email ? { ...i, role } : i));
  };

  const handleShareCompetition = async () => {
    if (inviteList.length === 0) {
      setShareError('Please add at least one email address');
      return;
    }

    const stored = localStorage.getItem('user');
    if (!stored) return;
    const user = JSON.parse(stored);
    if (!user?.id || !shareCompetitionId) return;

    setSharing(true);
    setShareError('');

    try {
      const payload = inviteList.map(i => ({ email: i.email, role: i.role }));
      const res = await fetch(api(`/api/competitions/${user.id}/${shareCompetitionId}/share`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails: payload }),
      });

      if (res.ok) {
        handleCloseShareDialog();
      } else {
        // Simple fallback: prefer response text, otherwise a generic message.
        let textBody = '';
        try {
          textBody = await res.text();
        } catch (e) {
          // ignore
        }
        console.warn('Share competition failed', res.status, textBody);
        const out = textBody && textBody.trim() && textBody.trim() !== 'null' ? textBody.trim() : 'Failed to share competition';
        setShareError(out);
      }
    } catch (error) {
      setShareError('Failed to share competition. Please try again.');
    } finally {
      setSharing(false);
    }
  };

  const handleEmailKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    // tabs removed; kept for compatibility but no-op
    return;
  };

  const renderCompetitionList = (competitions: Competition[]) => (
    <List sx={{ pt: 0 }}>
      {competitions.map((comp, index) => (
        <ListItem
          key={comp.id}
          divider
          secondaryAction={
            // Reserve a consistent width for action icons so list text aligns across rows.
            comp.role === 'host' ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton edge="end" onClick={(e) => { e.stopPropagation(); handleOpenShareDialog(comp.id); }} sx={{ mr: 1 }}>
                  <ShareIcon />
                </IconButton>
                <IconButton edge="end" onClick={(e) => { e.stopPropagation(); handleEdit(comp); }} sx={{ mr: 1 }}>
                  <EditIcon />
                </IconButton>
                <IconButton edge="end" onClick={(e) => { e.stopPropagation(); handleRequestDelete(comp.id, false); }} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            ) : (
              // Participant: allow leaving the competition (delete/remove self)
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton edge="end" onClick={(e) => { e.stopPropagation(); handleRequestDelete(comp.id, true); }} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            )
          }
          sx={(theme) => ({
            pr: comp.role === 'host' ? 16 : 6,
            py: 0,
            alignItems: 'center',
            ...(index === 0 && { pt: 0 }),
            // ensure hover background covers the whole list row including secondaryAction
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
            cursor: 'pointer',
            minHeight: 72,
          })}
          disablePadding
          onClick={() => history.push(`/competitions/${comp.id}`, { name: comp.name, type: comp.type })}
        >
          <ListItemButton
            component="div"
            disableRipple
            sx={{ height: '100%', pt: 0.25, pb: 0.75, flex: 1, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 0, '&:hover': { backgroundColor: 'transparent' } }}
          >
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'center' }}>
                <ListItemText 
                  primary={comp.name} 
                  secondary={comp.description || ' '} 
                  primaryTypographyProps={{ sx: { mt: 0, mb: 0 } }}
                  secondaryTypographyProps={{ sx: { mt: 0 } }}
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 0.25, mb: 1, alignItems: 'center' }}>
                  <Chip label={comp.type || 'Unknown'} size="small" />
                  <Chip label={comp.role === 'host' ? 'Host' : 'Participant'} size="small" color={comp.role === 'host' ? 'primary' : 'default'} />
                  <Chip
                    label={
                      comp.status === 'started' ? 'Started' : comp.status === 'stopped' ? 'Stopped' : comp.status === 'published' ? 'Published' : 'Draft'
                    }
                    size="small"
                    color={
                      comp.status === 'published' ? 'success' : comp.status === 'started' ? 'warning' : comp.status === 'stopped' ? 'default' : 'default'
                    }
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Box>
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('=== SUBMIT CALLED ===');
    console.log('isSubmitting:', isSubmitting);
    console.log('Timestamp:', new Date().toISOString());
    
    if (isSubmitting) {
      console.log('Already submitting, ignoring');
      return;
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Header onMenuClick={() => setDrawerOpen(true)} title="Wine War" />
      
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        <Divider />
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMenuItemClick('/home')}>
              <ListItemIcon>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText primary="Home" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMenuItemClick('/competitions')}>
              <ListItemIcon>
                <EmojiEventsIcon />
              </ListItemIcon>
              <ListItemText primary="Battles" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMenuItemClick('/settings')}>
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
      
      <Box component="main" sx={{ flexGrow: 1, p: 4, mt: 8 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5">Battles</Typography>
          <Box sx={{ mt: 1 }}>
            <Button variant="contained" onClick={handleOpenDialog}>
              New battle
            </Button>
          </Box>
        </Box>

        {loading && <Typography>Loading...</Typography>}

        {!loading && (
          <Box>
            {allCompetitions.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                You have no battles yet.
              </Typography>
            ) : (
              renderCompetitionList(allCompetitions)
            )}
          </Box>
        )}

        {error && <Typography color="error">{error}</Typography>}

        <Dialog open={confirmOpen} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
          <DialogTitle>
            {(window as any).isDeletingShared ? 'Leave battle?' : 'Delete battle?'}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {(window as any).isDeletingShared 
                ? 'Are you sure you want to leave this battle? You can be re-invited later.'
                : 'Are you sure you want to delete this battle? This action cannot be undone.'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete}>Cancel</Button>
            <Button color="error" onClick={handleConfirmDelete}>
              {(window as any).isDeletingShared ? 'Leave' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={shareDialogOpen} onClose={handleCloseShareDialog} fullWidth maxWidth="sm">
          <DialogTitle>Share Battle</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Enter email addresses to invite people to this battle. They will receive an invitation link.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Email address"
                fullWidth
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                onKeyPress={handleEmailKeyPress}
                error={!!shareError}
                helperText={shareError}
                disabled={sharing}
              />
              <Button 
                variant="outlined" 
                onClick={handleAddEmail}
                disabled={sharing || !emailInput.trim()}
              >
                Add
              </Button>
            </Box>
            {inviteList.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {inviteList.map(item => (
                  <Box key={item.email} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.5, borderRadius: 1, background: 'rgba(0,0,0,0.03)' }}>
                    <Chip label={item.email} disabled={sharing} />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel id={`role-label-${item.email}`}>Role</InputLabel>
                      <Select
                        labelId={`role-label-${item.email}`}
                        value={item.role}
                        label="Role"
                        onChange={(e) => handleChangeRole(item.email, e.target.value as 'host' | 'participant')}
                        disabled={sharing}
                      >
                        <MenuItem value="participant">Participant</MenuItem>
                        <MenuItem value="host">Host</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton size="small" onClick={() => handleRemoveEmail(item.email)} disabled={sharing}>
                      ✕
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseShareDialog} disabled={sharing}>
              Cancel
            </Button>
            <Button 
              onClick={handleShareCompetition} 
              variant="contained" 
              disabled={sharing || inviteList.length === 0}
            >
              {sharing ? 'Sharing...' : 'Share'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
          <DialogTitle>{editingCompetition ? 'Edit Battle' : 'New Battle'}</DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <TextField
              label="Battle name"
              fullWidth
              margin="normal"
              value={newName}
              onChange={e => setNewName(e.target.value)}
            />
            <FormControl sx={{ width: '100%', mt: 2 }} size="medium">
              <InputLabel id="competition-type-label">Type</InputLabel>
              <Select
                labelId="competition-type-label"
                label="Type"
                value={newType}
                onChange={(e) => setNewType(e.target.value as 'Wine' | 'Food')}
              >
                <MenuItem value="Wine">Wine</MenuItem>
                <MenuItem value="Food">Food</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Max entries"
              type="number"
              fullWidth
              margin="normal"
              value={maxEntries}
              onChange={e => setMaxEntries(Number(e.target.value))}
              inputProps={{ min: 1, max: 50 }}
            />
            <TextField
              label="Max votes per person"
              type="number"
              fullWidth
              margin="normal"
              value={maxVotesPerPerson}
              onChange={e => setMaxVotesPerPerson(Number(e.target.value))}
              inputProps={{ min: 1, max: maxEntries }}
              helperText={`Participants can vote for up to ${maxVotesPerPerson} entries`}
            />
            <TextField
              label="Description"
              fullWidth
              margin="normal"
              value={newDescription}
              onChange={e => setNewDescription(e.target.value)}
              multiline
              minRows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleCreateCompetition} variant="contained" disabled={saving || !newName.trim()}>
              {editingCompetition ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default CompetitionsPage;